<template>
  <div>
    <H1 font-family="Bitter" color="#ffffff" center>
      {{ $t(`configurator.heading.${mode}`) }}
    </H1>
    <div class="container bg-white">
      <breadcrumbs :items="steps" :current-item="currentStep" @item-click="navigate" />
      <spinner
        v-if="isLoading"
        :color="configuratorData.PrimaryColor ? configuratorData.PrimaryColor : null"
        class="m-b-xxl m-t-xxl align-center"
      />
      <div v-if="!isLoading">
        <div v-if="currentStep === 'tourSelection'">
          <product-selection :products="products" @product-selected="onProductSelected" />
        </div>
        <div v-if="currentStep === 'extraSelection'">
          <extra-selection
            :extras="extras"
            :orderTitle="orderData.product.Title"
            :selected-extras="order.extras"
            @extras-selected="onExtrasSelected"
          />
        </div>
        <div v-if="currentStep === 'upsellSelection'">
          <upsell-selection
            :upsells="upsells"
            :orderTitle="orderData.product.Title"
            :selected-upsells="order.upsells"
            :min-participants="orderData.product.MinParticipants"
            :max-participants="orderData.product.MaxParticipants"
            :peopleCount="orderData.details.peopleCount"
            :playDate="orderData.details.playDate"
            :playTime="orderData.details.playTime"
            :product="orderData.product"
            @form-data-filled="onGroupDataSelected"
            @upsells-selected="onUpsellsSelected"
          />
        </div>
        <div v-if="currentStep === 'checkout'">
          <customer-form
            :form-fields="customerPersonalDataForm"
            :order="orderData"
            :price-total="priceTotal"
            :price-per-person="pricePerPerson"
            :testimonials="testimonials"
            :mode="mode"
            @form-data-filled="onSubmit"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import spinner from '@/components/common/spinner';
import breadcrumbs from '@/components/common/breadcrumbs/breadcrumbs.vue';
import { H1 } from '@/components/common/styledHeading';
import productSelection from '@/components/configuratorParts/productSelection.vue';
import extraSelection from '@/components/configuratorParts/extraSelection.vue';
import upsellSelection from '@/components/configuratorParts/upsellSelection.vue';
import customerForm from '@/components/configuratorParts/customerForm.vue';
import customerPersonalDataForm from '@/config/customerPersonalDataForm';

export default {
  name: 'configurator',

  components: {
    spinner,
    breadcrumbs,
    H1,
    productSelection,
    extraSelection,
    upsellSelection,
    customerForm,
  },

  watch: {
    /**
     * Fetches extras and upsell products based on user product selection.
     */
    async selectedProductId() {
      this.isLoading = true;
      await Promise.all([
        this.fetchExtras({ productId: this.selectedProductId }),
        this.fetchUpsells({ productId: this.selectedProductId }),
        this.fetchTestimonials(),
      ]);
      this.isLoading = false;
    },
  },

  data() {
    return {
      isLoading: false,
      mode: '',
      steps: [
        {
          id: 'tourSelection',
          name: this.$t('configurator.chooseTour'),
        },
        {
          id: 'extraSelection',
          name: this.$t('configurator.extras'),
        },
        {
          id: 'upsellSelection',
          name: this.$t('configurator.specials'),
        },
        {
          id: 'checkout',
          name: this.$t('configurator.checkoutEmojis'),
        },
      ],
      currentStep: '',
      selectedProductId: '',
      customerPersonalDataForm,
    };
  },

  computed: {
    ...mapGetters({
      configuratorData: 'configurators/getConfiguratorData',
      products: 'configurators/getProducts',
      extras: 'configurators/getExtras',
      upsells: 'configurators/getUpsells',
      testimonials: 'configurators/getTestimonials',
      order: 'configurators/getOrder',
    }),

    /**
     * Obtains the IDs of the selected items from the order store and finds the matching items
     * in the respective store. In this way we avoid double data storage.
     */
    orderData() {
      const { details } = this.order;
      const product = this.products.find(e => e.id === this.order.product);
      const extras = this.order.extras.map(e => this.extras.find(extra => extra.id === e));
      const upsells = this.order.upsells.map(e => this.upsells.find(upsell => upsell.id === e));
      return {
        details,
        product,
        extras,
        upsells,
      };
    },

    priceTotal() {
      const items = [this.orderData.product, ...this.orderData.extras, ...this.orderData.upsells];
      return this.calcPriceTotal(items, this.orderData.details.peopleCount);
    },

    pricePerPerson() {
      return this.priceTotal / this.orderData.details.peopleCount;
    },
  },

  beforeMount() {
    const { productId } = this.$route.query;
    if (productId) {
      this.onProductSelected(productId);
      return;
    }
    this.currentStep = 'tourSelection';
  },

  async mounted() {
    this.isLoading = true;
    this.setBookingMode(this.$route.query.mode);
    await this.fetchProducts({ configuratorId: this.configuratorData.id });
    this.isLoading = false;
  },

  methods: {
    ...mapActions('configurators', {
      fetchProducts: 'fetchProducts',
      fetchExtras: 'fetchExtras',
      fetchUpsells: 'fetchUpsells',
      fetchTestimonials: 'fetchTestimonials',
      postOrder: 'postOrder',
      setSelectedProduct: 'setSelectedProduct',
      setSelectedExtras: 'setSelectedExtras',
      setSelectedUpsells: 'setSelectedUpsells',
      setOrderDetails: 'setOrderDetails',
      removeSelectedExtras: 'removeSelectedExtras',
      removeSelectedUpsells: 'removeSelectedUpsells',
    }),

    navigate(to) {
      // early exit
      if (this.currentStep === to) return;
      // if current step ist the first one, don't allow navigation
      if (this.currentStep === 'tourSelection') return;
      // if the clicked step is checkout, don't allow navigation
      if (to === 'checkout') return;
      if (this.currentStep === 'extraSelection' && to === 'tourSelection') {
        this.removeSelectedExtras();
      }
      if (this.currentStep === 'upsellSelection' && to === 'extraSelection') {
        this.removeSelectedUpsells();
      }
      if (this.currentStep === 'checkout' && to === 'upsellSelection') {
        this.removeSelectedUpsells();
      }
      this.currentStep = to;
    },

    setBookingMode(mode) {
      switch (mode) {
        case 'booking':
          this.mode = 'booking';
          break;
        case 'inquiry':
          this.mode = 'inquiry';
          break;
        default:
          this.mode = 'configurator';
      }
    },

    onProductSelected(productId) {
      this.selectedProductId = productId;
      this.setSelectedProduct({ productId });
      this.currentStep = 'extraSelection';
    },

    onExtrasSelected(selectedExtras) {
      this.setSelectedExtras({ selectedExtras });
      this.currentStep = 'upsellSelection';
    },

    onGroupDataSelected(formData) {
      this.setOrderDetails({ formData });
    },

    onUpsellsSelected(selectedUpsells) {
      this.isLoading = true;
      this.setSelectedUpsells({ selectedUpsells });
      this.currentStep = 'checkout';
      this.isLoading = false;
    },

    async onSubmit(formData) {
      this.isLoading = true;
      this.setOrderDetails({ formData });
      await this.postOrder({
        configuratorId: this.configuratorData.id,
        details: this.order.details,
        selectedProduct: this.order.product,
        selectedExtras: this.order.extras,
        selectedUpsells: this.order.upsells,
        orderValue: this.priceTotal,
      });
      this.trackConversion({
        zip: this.orderData.details.zipCode,
        pricePerPerson: this.pricePerPerson,
        peopleCount: this.orderData.details.peopleCount,
        value: this.priceTotal,
        event: this.order.details.isRequest ? 'lead' : 'purchase',
        product: this.orderData.product.Title,
        productValue: this.orderData.product.Price,
      });
      this.$router.push({ name: 'thank-you' });
      this.isLoading = false;
    },

    trackConversion(layerData) {
      window.dataLayer.push(layerData);
    },

    calcPriceTotal(items, peopleCount) {
      return items.reduce(
        (accumulator, { PriceType, Price }) =>
          accumulator + (PriceType !== 'Flat' ? Price * peopleCount : Price),
        0,
      );
    },
  },
};
</script>
